"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.referencePoint = referencePoint;
exports.localizationNetClearance = localizationNetClearance;
exports.localizationHeadHeight = localizationHeadHeight;
/**
 * User-aided localization: the reference points vision is pointed at, and
 * the net clearance and head height that keep the camera's line of sight to
 * them over the net. Racquet venues only, in practice: these refuse a venue
 * with no net.
 *
 * The arithmetic follows v1 (TrainerSim.GetUserAidedLocalizationHeight,
 * util.calculateNetClearanceHeight) to the bit.
 */
const camera_1 = require("./camera");
const coordinates_1 = require("./coordinates");
/** A named reference point of the venue (v1 getCourtPointByName), if it has one. */
function referencePoint({ frame: { referencePoints } }, name) {
    return Object.hasOwn(referencePoints, name) ? referencePoints[name] : undefined;
}
function requireNet(venue) {
    if (!venue.arena.panels.some((panel) => panel.kind === "net")) {
        throw new RangeError(`${venue.sport} has no net to see over`);
    }
}
const MIN_CLEARANCE = 1.0; // ft, from the baseline back
const MAX_CLEARANCE = 3.0; // ft, at the net
/**
 * The net clearance, in whole feet, user-aided localization should ask for
 * with the trainer at `trainerY` (v1 calculateNetClearanceHeight): 3 ft at
 * the net, falling linearly to 1 ft at the trainer-side edge of the stage
 * (the near baseline) and beyond. Throws on a venue with no net, where v1
 * answers with a number that means nothing.
 */
function localizationNetClearance(venue, trainerY) {
    requireNet(venue);
    const { stage } = venue;
    const fromNet = Math.min(Math.abs(trainerY) / -stage.extents.y[0], 1.0);
    const clearance = MAX_CLEARANCE - fromNet * (MAX_CLEARANCE - MIN_CLEARANCE);
    return Math.floor(Math.max(MIN_CLEARANCE, Math.min(MAX_CLEARANCE, clearance)));
}
const FEET_PER_METER = 3.281; // v1's rounding
const FAR_COURT_MIN_Y = 0.1; // m
const NEAR_COURT_MAX_Y = -0.01; // m
/** Clamped heights stop this fraction of the arm short of its ends. */
const REACH_MARGIN = 0.99;
/**
 * The head height at which the camera's line of sight to the far-court
 * reference point `pointName` passes `clearanceFeet` above the net post tops
 * (v1 GetUserAidedLocalizationHeight), clamped inside the arm's reach.
 *
 * Throws if the sport has no such point or no net, the point is not in the
 * far court, or the camera is not in the near court.
 */
function localizationHeadHeight(profile, localized, pointName, clearanceFeet) {
    const { venue } = profile;
    const { geometry } = profile.trainer;
    const target = referencePoint(venue, pointName);
    if (target === undefined) {
        throw new RangeError(`${pointName} is not a ${venue.sport} reference point`);
    }
    const cam = (0, coordinates_1.fromLocalization)(venue, localized);
    const netPost = referencePoint(venue, "netpost_r_t");
    if (netPost === undefined) {
        throw new RangeError(`${venue.sport} has no net to see over`);
    }
    if (!(target.y >= FAR_COURT_MIN_Y)) {
        throw new RangeError(`${pointName} (y = ${target.y} m) must be at least ${FAR_COURT_MIN_Y} m into the far court`);
    }
    if (!(cam.y <= NEAR_COURT_MAX_Y)) {
        throw new RangeError(`the camera (y = ${cam.y} m) must be at least ${-NEAR_COURT_MAX_Y} m into the near court`);
    }
    // The sight line from camera to point crosses the net plane (y = 0) at
    // parameter t; solve for the camera height that puts it at clearZ there.
    const clearZ = netPost.z + clearanceFeet / FEET_PER_METER;
    const t = -cam.y / (target.y - cam.y);
    const headHeight = (0, camera_1.headHeightForCamera)(geometry, (clearZ - t * target.z) / (1.0 - t));
    const { liftPivot, liftArmLength: arm } = geometry;
    const fromPivot = headHeight - liftPivot.z;
    if (fromPivot > arm) {
        return REACH_MARGIN * arm + liftPivot.z;
    }
    if (fromPivot < -arm) {
        return liftPivot.z - REACH_MARGIN * arm;
    }
    return headHeight;
}
